"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Geist_Mono } from "next/font/google";
import { geist } from "@/helpers/geist";
import { cn } from "@/lib/utils";
import { CHANGELOG_SCENES, ChangelogSceneKey, changelogSceneUrl } from "./scenes/sceneConfig";
import { playChangelogScene, stopChangelogScene } from "./scenes/sceneChoreography";

// The scene SVGs set their text in Geist / Geist Mono (Geist comes from the shared helper). Not
// preloaded: the shared catch-all route would otherwise preload them on every page.
const geistMono = Geist_Mono({ subsets: ["latin"], variable: "--font-geist-mono", display: "swap", preload: false });

const sceneMarkup = new Map<ChangelogSceneKey, Promise<string>>();

/** Fetches a scene's SVG text once per page load (shared across mounts and Strict Mode re-runs). */
function loadSceneMarkup(key: ChangelogSceneKey) {
  let request = sceneMarkup.get(key);
  if (!request) {
    request = fetch(changelogSceneUrl(key)).then((response) => {
      if (!response.ok) throw new Error(`Scene "${key}" responded ${response.status}`);
      return response.text();
    });
    request.catch(() => sceneMarkup.delete(key));
    sceneMarkup.set(key, request);
  }
  return request;
}

function parseScene(markup: string) {
  const doc = new DOMParser().parseFromString(markup, "image/svg+xml");
  const root = doc.documentElement;
  if (!(root instanceof SVGSVGElement) || doc.querySelector("parsererror")) return null;
  return document.importNode(root, true);
}

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function ChangelogScene({
  scene,
  label,
  fallback,
  className,
}: {
  scene: ChangelogSceneKey;
  /** Accessible name of the scene (the entry title). */
  label: string;
  /** Rendered instead of the scene if its SVG cannot be loaded. */
  fallback?: React.ReactNode;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef(label);
  const runningRef = useRef(false);
  const [failedScene, setFailedScene] = useState<ChangelogSceneKey | null>(null);
  const { width, height } = CHANGELOG_SCENES[scene];

  const play = useCallback(
    (svg: SVGSVGElement) => {
      runningRef.current = true;
      playChangelogScene(svg, scene).then(() => {
        runningRef.current = false;
      });
    },
    [scene],
  );

  useEffect(() => {
    labelRef.current = label;
    hostRef.current?.querySelector("svg")?.setAttribute("aria-label", label);
  }, [label]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;
    let svg: SVGSVGElement | null = null;
    let observer: IntersectionObserver | undefined;

    loadSceneMarkup(scene)
      .then((markup) => {
        if (cancelled) return;
        const parsed = parseScene(markup);
        if (!parsed) throw new Error(`Scene "${scene}" is not a valid SVG`);

        parsed.classList.add("scene-svg");
        parsed.setAttribute("role", "img");
        parsed.setAttribute("aria-label", labelRef.current);
        host.dataset.state = "idle";
        host.replaceChildren(parsed);
        svg = parsed;

        if (prefersReducedMotion()) {
          host.dataset.state = "playing";
          return;
        }

        const target = parsed;
        observer = new IntersectionObserver(
          ([entry]) => {
            if (!entry?.isIntersecting) return;
            observer?.disconnect();
            play(target);
            host.dataset.state = "playing";
          },
          { threshold: 0.25 },
        );
        observer.observe(host);
      })
      .catch(() => {
        if (!cancelled) setFailedScene(scene);
      });

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (svg) stopChangelogScene(svg);
      runningRef.current = false;
      host.replaceChildren();
    };
  }, [scene, play]);

  // Hover replays the scene once the first run has finished (mouse/pen only).
  const handlePointerEnter = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch" || runningRef.current) return;
    const host = hostRef.current;
    const svg = host?.querySelector("svg");
    if (!host || !svg || host.dataset.state !== "playing" || prefersReducedMotion()) return;
    play(svg);
  };

  if (failedScene === scene) return <>{fallback}</>;

  return (
    <div
      ref={hostRef}
      data-state="idle"
      onPointerEnter={handlePointerEnter}
      style={{ aspectRatio: `${width} / ${height}` }}
      className={cn(
        "scene relative w-full overflow-hidden",
        geist.variable,
        geistMono.variable,
        "[&>svg]:block [&>svg]:h-auto [&>svg]:w-full [&[data-state=idle]>svg]:invisible",
        "[&_text[font-family='Geist']]:[font-family:var(--font-geist),'Geist',sans-serif]",
        "[&_text[font-family='Geist_Mono']]:[font-family:var(--font-geist-mono),'Geist_Mono',monospace]",
        "[&_text[font-family='Helvetica_Neue']]:[font-family:var(--font-helvetica-neue),'Helvetica_Neue',sans-serif]",
        className,
      )}
    />
  );
}
